import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import Image from "next/image"

/**
 * Renders a component that indicates that the feature is not supported.
 *
 * @returns {null} Returns null.
 */
const NotSupported = () => null

/**
 * Applies the current locale prefix to a relative URL from Contentful.
 * External URLs (http/https) are returned unchanged.
 *
 * @param {string} url - The raw URL from Contentful.
 * @param {string} locale - The active Next.js locale (e.g. "en" | "id").
 * @returns {string} The URL with the correct locale prefix.
 */
export const applyLocaleToUrl = (url, locale) => {
  if (!url || !locale) return url
  if (
    url.includes("http") ||
    url.startsWith("//") ||
    url.includes("mailto") ||
    url.includes("tel")
  )
    return url
  const localePattern = /^\/(en|id)(\/|$)/
  if (localePattern.test(url)) {
    return url.replace(localePattern, `/${locale}$2`)
  }
  return `/${locale}${url.startsWith("/") ? url : `/${url}`}`
}

/**
 * Renders rich text content using the Contentful rich text renderer.
 * @param {object} copy - The rich text content to be rendered.
 * @returns {React.Component} - The rendered React components.
 */

export const renderRichText = (copy, locale) => {
  if (!copy) {
    return null
  }
  return documentToReactComponents(copy, {
    renderMark: {
      strong: _text => <strong className="!font-[600]">{_text}</strong>,
      bold: _text => <b className="!font-[600]">{_text}</b>,
      italic: _text => <i>{_text}</i>,
      underline: _text => <u>{_text}</u>,
      code: text => <span>{text}</span>,
      superscript: _text => <sup>{_text}</sup>,
      subscript: _text => <sub>{_text}</sub>,
      strikethrough: NotSupported,
    },
    renderNode: {
      paragraph: (_node, children) => <p className="mb-3">{children}</p>,
      "heading-1": (_node, children) => (
        <h1 className="text-[35px] mb-3">{children}</h1>
      ),
      "heading-2": (_node, children) => (
        <h2 className="text-[30px] mb-3">{children}</h2>
      ),
      "heading-3": (_node, children) => (
        <h3 className="text-[24px] mb-3">{children}</h3>
      ),
      "heading-4": (_node, children) => (
        <h4 className="text-[21px] mb-3">{children}</h4>
      ),
      "heading-5": (_node, children) => (
        <h5 className="text-[18px] mb-3">{children}</h5>
      ),
      "heading-6": (_node, children) => <h6>{children}</h6>,

      hyperlink: (_node, children) => {
        const rawUrl = _node?.data?.uri || ""
        const formattedUrl = rawUrl.replace(/\.html(?=\?|$)/, "")
        const isExternal =
          rawUrl.includes("http") ||
          rawUrl.includes("mailto") ||
          rawUrl.includes("tel")
        const isBold = _node.content?.some(child =>
          child.marks?.some(
            mark => mark.type === "bold" || mark.type === "strong"
          )
        )

        const resolvedUrl = isExternal
          ? formattedUrl
          : applyLocaleToUrl(formattedUrl, locale)

        return (
          <a
            href={resolvedUrl}
            target={isExternal ? "_blank" : "_self"}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={e => {
              if (isExternal) {
                e.preventDefault()
                e.stopPropagation()
                window.open(resolvedUrl, "_blank", "noopener,noreferrer") // ✅ force new tab
              }
            }}
            className={`link ${isBold ? "!font-[600]" : ""}`}
          >
            {children}
          </a>
        )
      },
      "ordered-list": (_node, children) => (
        <ol className="list-decimal pl-6 mb-6 space-y-2">{children}</ol>
      ),
      "unordered-list": (_node, children) => (
        <ul className="list-[circle] pl-10 mb-6 space-y-2">{children}</ul>
      ),
      "list-item": (_node, children) => (
        <li className="text-[15px] mb-0 leading-[26px] text-[#333] [&>p]:m-0 [&>p]:inline">
          {children}
        </li>
      ),
      hr: NotSupported,
      blockquote: NotSupported,
      "embedded-entry-block": NotSupported,
      "embedded-asset-block": NotSupported,
      "embedded-resource-block": NotSupported,
      table: NotSupported,
      "table-row": NotSupported,
      "table-cell": NotSupported,
      "table-header-cell": NotSupported,
    },
    renderText: text => {
      return text.split("\n").reduce((children, textSegment, index) => {
        const preservedSpaces = textSegment.replace(/ {2,}/g, match =>
          Array.from(match)
            .map((_, i) => (i % 2 === 0 ? "\u00A0" : " "))
            .join("")
        )
        return [...children, index > 0 && <br key={index} />, preservedSpaces]
      }, [])
    },
  })
}

export const formatImage = image => ({
  src: image?.imageUrl,
  alt: image?.altText || "",
  loading: "lazy",
  component: Image,
  fill: true,
  width: null,
  position: null,
  height: null,
  style: null,
})

export const normalizeSpaces = (s = "") =>
  s
    .replace(
      /[\u00A0\u202F\u2007\u2009\u200A\u200B\u2060\uFEFF\u2028\u2029]/g,
      " "
    )
    .trim()

export const formatHeading = heading => {
  if (!heading) return null

  const rawText = heading?.fields?.text?.replace(/\\n/g, "\n")
  //to prevent whitespaces pasted into CMS (NBSP, narrow NBSP, zero‑width, BOM, paragraph separator), consider a broader sanitizer
  const result = normalizeSpaces(rawText)
  return {
    text: result,
    component: heading?.fields?.component || "h1",
  }
}

export const formatCTA = (cta, locale) => {
  if (!cta) {
    return null
  }
  const { url = "", label = "", openerType = "" } = cta?.fields || {}
  const targets =
    openerType === "New Tab" || url?.includes("http") ? "_blank" : "_self"
  return {
    href: applyLocaleToUrl(cta?.fields?.url, locale),
    text: label,
    target: targets,
    role: "link",
  }
}

export const formatMultiColumnContentCards = (cards = [], locale) => {
  return cards?.map(card => ({
    heading: {
      text: card?.fields?.heading?.fields?.text,
      component: "h3",
    },
    copy: { text: card?.fields?.copy },
    CTA: formatCTA(card?.fields?.cta, locale),
    image: {
      src:
        card?.fields?.image?.fields?.imageUrl ||
        card?.fields?.image?.fields?.asset?.fields?.file?.url,
      alt: card?.fields?.image?.fields?.altText || "",
      loading: "lazy",
      role: "presentation",
    },
  }))
}

export const formatFullBleedHeroVideo = (video, poster, posterImages) => {
  return video?.fields?.file?.fields?.file?.url
    ? {
        src: video?.fields?.file?.fields?.file?.url,
        poster: poster?.fields?.file?.url,
        posterImages: posterImages?.map(posterImage => {
          return { srcSet: posterImage?.fields?.file?.url }
        }),
        autoPlay: true,
        loop: true,
        controls: false,
        muted: true,
      }
    : undefined
}

/**
 * Formats the breadcrumbs array into an array of objects with text and href properties.
 *
 * @param {Array} breadcrumbs - The array of breadcrumbs to be formatted.
 * @returns {Array} - The formatted array of breadcrumb objects.
 */
export const formatBreadcrumbs = (breadcrumbs = []) => {
  return breadcrumbs?.map(breadcrumb => {
    return {
      text: breadcrumb?.fields?.label,
      href: breadcrumb?.fields?.href,
    }
  })
}
